/**
 * Karbon360° v2 — CBAM overview.
 *
 * The analysis surface of the module: everything here is read from
 * GET /api/cbam/summary?year= and never recomputed from raw records — the
 * service is the single place the Annex III arithmetic lives, and this screen's
 * job is to show its work. Per good: the SEE table with the chargeable split
 * (aluminium/steel/hydrogen owe certificates on DIRECT emissions only — the
 * indirect column is a memo for those sectors), a quality badge (any
 * default-valued precursor costs the 'actual' tier), and a drawer with the full
 * calculation trace, re-verified on screen by CalcTrace.
 *
 * The cost panel turns the server's certificate curve into money: the eur
 * figures are certificates × max(0, ETS price − carbon price due at origin,
 * Art 9), so the price assumption is editable HERE — rescaling the curve
 * client-side is exact arithmetic, not a re-estimate. A typed override also
 * rides on every export URL as ?ets_price=, so the workbook's and trace PDF's
 * indicative cost columns match the screen; nothing is persisted server-side.
 *
 * LANGUAGE: English in both dictionaries. This surface quotes the regulation
 * and feeds artefacts that leave the building (workbook, trace PDF) — same
 * decision as the CN-lookup read-out and the exports themselves.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ErrorBoundary from '../../components/ErrorBoundary';
import { Shell } from '../../components/Shell';
import { X } from '../../components/v2/Icons';
import {
  PageHeader, Card, Button, Stat, Badge, Callout, Empty, Skeleton,
  Select, UnitInput, TableWrap, Drawer, DList, Sect, CalcTrace, useToast, fmt,
} from '../../components/v2/ui';
import { useSession } from '../../components/v2/session';
import { useT } from '../../i18n';
import CbamGate from './CbamGate';
import { CBAM_BASE_YEAR, CBAM_YEARS, sectorLabel, num, QUALITY_TONE } from './cbam-shared';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/**
 * Art 9 deduction: the carbon price effectively paid at origin comes off the
 * ETS price before certificates turn into euros. The server's eur figures
 * already embed it, so every client-side rescale from certificates must too —
 * otherwise a good from a carbon-priced country looks dearer on screen than
 * in the workbook. `carbon_price_due_eur` is €/tCO₂e, 0 when none.
 */
const effectivePrice = (g, price) => Math.max(0, price - (num(g.carbon_price_due_eur) || 0));

/**
 * Categorical colours for the cost-curve segments — same family as the scope
 * colours in Charts.jsx. Green and red stay semantic (quality badges), never
 * decorative, so neither appears here.
 */
const GOOD_COLORS = ['#6366F1', '#0EA5E9', '#F59E0B', '#8B5CF6', '#14B8A6', '#F97316', '#3B82F6', '#A855F7'];

/**
 * Chargeability by sector (Annex II of Reg 2023/956): cement + fertilisers owe
 * direct AND indirect; aluminium, iron/steel and hydrogen owe direct only. The
 * server's flag wins when it is present — this fallback only covers older
 * summary payloads that predate the field.
 */
const indirectCharged = (g) =>
  (g.chargeable_indirect !== null && g.chargeable_indirect !== undefined)
    ? !!g.chargeable_indirect
    : (g.sector === 'cement' || g.sector === 'fertilisers');

/**
 * Where a completeness finding gets fixed. Keys are the server's; matching on
 * fragments keeps this map honest when the API grows new checks — an unknown
 * key lands on the installations screen, the start of the data chain.
 */
const fixRoute = (key) => {
  const k = String(key || '').toLowerCase();
  if (/good|precursor|cn_|export|tonn/.test(k)) return '/cbam/goods';
  return '/cbam/installations';
};

/** Blob → anchor download with the server's Content-Disposition filename (EmissionsDashboard.js pattern). */
async function downloadFile(url, fallbackName) {
  const r = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const blob = await r.blob();
  const cd = r.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="([^"]+)"/);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = m ? m[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

/* ── cost curve (hand-rolled SVG, Charts.jsx conventions — no chart libs) ── */

function CostCurve({ years }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(...years.map((y) => y.total), 0.0001);
  const W = 100;
  const slot = W / Math.max(years.length, 1);
  const barW = slot * 0.55;

  return (
    <div>
      <svg viewBox={`0 0 ${W} 46`} preserveAspectRatio="none" style={{ width: '100%', height: 200, display: 'block' }} role="img" aria-label="Indicative CBAM certificate cost per year">
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <line key={g} x1="0" x2={W} y1={40 - g * 38} y2={40 - g * 38} stroke="var(--n100)" strokeWidth="0.25" />
        ))}
        {years.map((y, i) => {
          const x = i * slot + (slot - barW) / 2;
          let acc = 0;
          return (
            <g key={y.year} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={i * slot} y={0} width={slot} height={40} fill={hover === i ? 'var(--k2-hover)' : 'transparent'} />
              {y.segments.map((s) => {
                const h = (Math.max(0, s.eur) / max) * 38;
                acc += h;
                return (
                  <rect key={s.id} x={x} y={40 - acc} width={barW} height={h} fill={s.color} rx="0.4">
                    <title>{`${y.year} · ${s.cn}: ${fmt(s.eur, 0)} €`}</title>
                  </rect>
                );
              })}
            </g>
          );
        })}
        <line x1="0" x2={W} y1="40" y2="40" stroke="var(--n200)" strokeWidth="0.3" />
      </svg>

      {/* x-axis: the year AND the CBAM factor — the phase-in is the story of this chart. */}
      <div style={{ display: 'flex', marginTop: 6 }}>
        {years.map((y, i) => (
          <div key={y.year} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 10.5, color: hover === i ? 'var(--n900)' : 'var(--n500)' }}>{y.year}</div>
            <div className="k2-num" style={{ fontSize: 9.5, color: 'var(--n400)' }}>
              {y.chargeablePct === null ? '' : `${y.chargeablePct}%`}
            </div>
          </div>
        ))}
      </div>

      {/* Detail row is ALWAYS rendered (defaults to the first year) so hovering
          never inserts an element and shifts the chart — it only swaps the text. */}
      {years.length > 0 && (() => {
        const sel = years[hover] ? years[hover] : years[0];
        return (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--n600)', minHeight: 18, whiteSpace: 'nowrap', overflow: 'hidden' }} className="k2-num">
            {sel.year} · phase-in {sel.chargeablePct ?? '—'}% —{' '}
            <b style={{ color: 'var(--n900)' }}>{fmt(sel.total, 0)} €</b>
            {sel.segments.filter((s) => s.eur > 0).map((s) => (
              <span key={s.id}> · {s.cn} {fmt(s.eur, 0)} €</span>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

/* ── trace rows from the server's trace[] ────────────────────────────────── */

function TraceList({ rows }) {
  return (
    <div>
      {rows.map((r, i) => {
        const v = num(r.value);
        return (
          <div key={`${r.label}-${i}`} style={{ display: 'flex', gap: 12, alignItems: 'baseline', padding: '7px 0', borderBottom: i < rows.length - 1 ? '1px solid var(--n100)' : 'none', fontSize: 12.5 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 550, color: 'var(--n800)' }}>{r.label}</div>
              {r.detail && <div style={{ fontSize: 11.5, color: 'var(--n500)' }}>{r.detail}</div>}
              {r.source && <div style={{ fontSize: 11, color: 'var(--n400)' }}>{r.source}</div>}
            </div>
            <div className="k2-num" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
              {v === null ? String(r.value ?? '—') : fmt(v, 3)}
              {r.unit && <span style={{ color: 'var(--n400)', fontWeight: 400, fontSize: 11 }}> {r.unit}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── good drawer: the SEE arithmetic, shown and re-verified ──────────────── */

function GoodDrawer({ g, year, price, etsParam, onClose }) {
  const { t } = useT();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const prod = num(g.production_tonnes);
  const entry = (g.cost_curve || []).find((c) => Number(c.year) === year) || (g.cost_curve || [])[0];
  const cert = entry ? num(entry.certificates_tco2e) : null;
  const eur = cert !== null ? cert * effectivePrice(g, price) : (entry ? num(entry.eur) : null);
  const charged = indirectCharged(g);

  const precCountry = (c) => (c === 'other' ? 'Other (fallback)' : c === 'unknown' ? 'Unknown origin' : (c || '—'));

  const downloadTracePdf = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const q = etsParam == null ? '' : `?ets_price=${etsParam}`;
      await downloadFile(`${API_URL}/api/cbam/goods/${g.id}/trace.pdf${q}`, `cbam-trace-${g.cn_code}-${g.year}.pdf`);
    } catch {
      toast(t('cbam.ov.exportFailed'), { tone: 'err' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      title={`${g.cn_code} — ${g.description || g.goods_category || 'CBAM good'}`}
      sub={`${sectorLabel(g.sector)} · ${g.year}`}
      badge={g.quality && <Badge tone={QUALITY_TONE[g.quality] || 'out'}>{g.quality}</Badge>}
      onClose={onClose}
      footer={
        <>
          <Button icon={X.Doc} disabled={busy} onClick={downloadTracePdf} style={{ marginRight: 'auto' }}>
            {busy ? t('cbam.ov.exporting') : t('cbam.ov.tracePdf')}
          </Button>
          <Button onClick={onClose}>{t('cbam.ov.close')}</Button>
        </>
      }
    >
      <Sect title={t('cbam.ov.seeTitle')}>
        <div className="k2-g2">
          <div className="k2-st">
            <div className="k2-lb2">Chargeable SEE</div>
            <div className="k2-v">{fmt(num(g.chargeable_see), 3)}<span className="k2-u">tCO₂e/t</span></div>
          </div>
          <div className="k2-st">
            <div className="k2-lb2">{t('cbam.ov.kpiCost', { year: entry ? entry.year : year })}</div>
            <div className="k2-v">{fmt(eur, 0)}<span className="k2-u">€</span></div>
          </div>
        </div>

        {/* SEE_g = attributed emissions ÷ activity level (Annex III Eq. 59) — re-derived on screen. */}
        <div style={{ marginTop: 12 }}>
          <CalcTrace
            activity={num(g.attributed_direct_tco2e) ?? 0}
            activityUnit="tCO₂e attributed (direct, incl. precursors)"
            factor={1}
            factorUnit=""
            divisor={prod || 1}
            result={num(g.see_direct) ?? 0}
            resultUnit="tCO₂e/t"
            tolerance={0.002}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <DList rows={[
            ['Category', g.goods_category || '—'],
            ['Production route', g.production_route || '—'],
            ['Production', `${fmt(prod, 1)} t`],
            ['EU export', g.eu_export_tonnes == null ? '—' : `${fmt(num(g.eu_export_tonnes), 1)} t`],
            ['Attributed direct', `${fmt(num(g.attributed_direct_tco2e), 3)} tCO₂e`],
            ['— of which precursors', `${fmt(num(g.precursor_direct_tco2e), 3)} tCO₂e`],
            ['Attributed indirect', `${fmt(num(g.attributed_indirect_tco2e), 3)} tCO₂e`],
            ['— of which precursors', `${fmt(num(g.precursor_indirect_tco2e), 3)} tCO₂e`],
            ['SEE direct', `${fmt(num(g.see_direct), 3)} tCO₂e/t`],
            ['SEE indirect', <span key="si">{fmt(num(g.see_indirect), 3)} tCO₂e/t{!charged && <span style={{ color: 'var(--n500)' }}> · {t('cbam.ov.indirectMemo')}</span>}</span>],
          ]} />
        </div>
      </Sect>

      {g.sector === 'aluminium' && (
        <div style={{ marginBottom: 18 }}>
          <Callout tone="info">{g.boundary_hint || t('cbam.ov.boundary')}</Callout>
        </div>
      )}

      {g.de_minimis_hint && (
        <div style={{ marginBottom: 18 }}>
          <Callout tone="info">{t('cbam.ov.deMinimis')}</Callout>
        </div>
      )}

      <Sect title={t('cbam.ov.traceTitle')}>
        {(g.trace || []).length > 0
          ? <TraceList rows={g.trace} />
          : <Callout tone="info">{t('cbam.ov.traceEmpty')}</Callout>}
      </Sect>

      <Sect title={t('cbam.ov.precTitle')}>
        {(g.precursors || []).length === 0 ? (
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--n500)' }}>{t('cbam.ov.precEmpty')}</p>
        ) : (
          (g.precursors || []).map((p, i) => (
            <div key={p.id || i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '7px 0', borderBottom: '1px solid var(--n100)', fontSize: 12.5 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 550, color: 'var(--n800)' }}>{p.name}</span>
                {p.cn_code && <span className="k2-num" style={{ color: 'var(--n500)' }}> · {p.cn_code}</span>}
                <div style={{ fontSize: 11.5, color: 'var(--n500)' }}>
                  {/* An internal row has no country or supplier — it is this
                      installation's own good, and says so instead of a dash. */}
                  {fmt(num(p.tonnes_per_tonne), 4)} t/t · {p.value_source === 'internal' ? t('cbam.prec.internalShort') : precCountry(p.supplier_country)}
                  {p.supplier_name && ` · ${p.supplier_name}`}
                </div>
              </div>
              {p.value_source === 'internal'
                ? <Badge tone="ok">{t('cbam.prec.internalShort')}</Badge>
                : p.eu_origin
                  ? <Badge tone="ok">SEE 0</Badge>
                  : p.value_source === 'default'
                    ? <Badge tone="warn">defaults</Badge>
                    : <Badge tone="ok">supplier</Badge>}
              <span className="k2-num" style={{ whiteSpace: 'nowrap', color: 'var(--n700)' }}>
                {p.see_direct == null ? '—' : `${fmt(num(p.see_direct), 3)} tCO₂e/t`}
              </span>
            </div>
          ))
        )}
      </Sect>
    </Drawer>
  );
}

/* ── the screen ──────────────────────────────────────────────────────────── */

function CbamOverviewBody({ year, summary, loading, error, price, setPriceOverride, priceOverride, onSelect }) {
  const { t } = useT();
  const navigate = useNavigate();

  const installations = useMemo(() => summary?.installations || [], [summary]);
  const goods = useMemo(() => summary?.goods || [], [summary]);

  /**
   * A typed 0 (or any non-positive value) can never be honoured: the export
   * endpoints reject ets_price <= 0, so etsParam stays null and the server
   * default applies on screen AND in the workbook/trace PDF. The math keeps
   * that fallback — this flag only makes the ignored input VISIBLE instead
   * of letting it sit there looking accepted.
   */
  const etsInvalid = priceOverride !== '' && !(Number(priceOverride) > 0);

  /** Stacked cost curve: one segment per good, eur recomputed from certificates × the live price. */
  const curve = useMemo(() => {
    const byYear = new Map();
    goods.forEach((g, gi) => {
      (g.cost_curve || []).forEach((c) => {
        const y = Number(c.year);
        if (!byYear.has(y)) byYear.set(y, { year: y, chargeablePct: num(c.chargeable_pct), segments: [], total: 0 });
        const row = byYear.get(y);
        if (row.chargeablePct === null) row.chargeablePct = num(c.chargeable_pct);
        const cert = num(c.certificates_tco2e);
        const eur = cert !== null ? cert * effectivePrice(g, price) : (num(c.eur) || 0);
        row.segments.push({ id: `${g.id}-${y}`, cn: g.cn_code, color: GOOD_COLORS[gi % GOOD_COLORS.length], eur });
        row.total += eur;
      });
    });
    return [...byYear.values()].sort((a, b) => a.year - b.year);
  }, [goods, price]);

  /**
   * The "defaults cost you money" lever. The resolvable part of the delta is
   * what the trace attributes to default values (the server tags those rows'
   * source); the supplier-actual path is unknowable until the supplier hands
   * over verified numbers, so the € figure is an "up to" bound, never a quote.
   */
  const lever = useMemo(() => goods
    .filter((g) => g.quality && g.quality !== 'actual')
    .map((g) => {
      const prod = num(g.production_tonnes);
      const defaultT = (g.trace || []).reduce((s, r) => {
        const v = num(r.value);
        return (v !== null && /default/i.test(String(r.source || '')) && /tco/i.test(String(r.unit || ''))) ? s + v : s;
      }, 0);
      const deltaSee = prod > 0 && defaultT > 0 ? defaultT / prod : null;
      const entry = (g.cost_curve || []).find((c) => Number(c.year) === year) || (g.cost_curve || [])[0];
      const exportT = num(g.eu_export_tonnes);
      const eur = deltaSee !== null && exportT && entry && num(entry.chargeable_pct) !== null
        ? deltaSee * exportT * (Number(entry.chargeable_pct) / 100) * price
        : null;
      return { g, deltaSee, eur };
    }), [goods, year, price]);

  if (error) return <Callout tone="err">{error}</Callout>;

  if (loading) {
    return (
      <>
        <div className="k2-g4" style={{ marginBottom: 24 }}>
          {[0, 1, 2, 3].map((i) => <Card key={i}><Skeleton h={54} /></Card>)}
        </div>
        <Card><Skeleton h={190} /></Card>
      </>
    );
  }

  if (installations.length === 0) {
    return (
      <Card>
        <Empty
          icon={X.Factory}
          title={t('cbam.ov.empty')}
          body={t('cbam.ov.emptyBody')}
          action={
            <Button variant="mint" icon={X.Plus} onClick={() => navigate('/cbam/installations')}>
              {t('cbam.ov.emptyCta')}
            </Button>
          }
        />
      </Card>
    );
  }

  const totalProduction = goods.reduce((s, g) => s + (num(g.production_tonnes) || 0), 0);
  const weightedSee = totalProduction > 0
    ? goods.reduce((s, g) => s + (num(g.see_direct) || 0) * (num(g.production_tonnes) || 0), 0) / totalProduction
    : null;
  const costYear = goods.reduce((s, g) => {
    const e = (g.cost_curve || []).find((c) => Number(c.year) === year) || (g.cost_curve || [])[0];
    if (!e) return s;
    const cert = num(e.certificates_tco2e);
    return s + (cert !== null ? cert * effectivePrice(g, price) : (num(e.eur) || 0));
  }, 0);

  const completeness = summary?.completeness || [];
  const missing = completeness.filter((c) => !c.ok);

  return (
    <>
      {/* ── KPI strip ── */}
      <div className="k2-g4" style={{ marginBottom: 24 }}>
        <Stat tone="dark" label={t('cbam.ov.kpiGoods')} value={goods.length} foot={`${installations.length} installation${installations.length === 1 ? '' : 's'} · ${year}`} />
        <Stat label={t('cbam.ov.kpiProduction')} value={fmt(totalProduction, 1)} unit="t" />
        <Stat label={t('cbam.ov.kpiSee')} value={fmt(weightedSee, 3)} unit="tCO₂e/t" foot="production-weighted" />
        <Stat
          label={t('cbam.ov.kpiCost', { year })}
          value={fmt(costYear, 0)}
          unit="€"
          foot={t('cbam.ov.kpiCostFoot', { price: fmt(price, 0) })}
        />
      </div>

      <div className="k2-g64">
        <div className="k2-gcol">
          {/* ── SEE per good ── */}
          <Card title={t('cbam.ov.goodsTitle')} sub={t('cbam.ov.goodsSub')}>
            {goods.length === 0 ? (
              <Empty
                icon={X.Layers}
                title={t('cbam.ov.noGoods', { year })}
                body={t('cbam.ov.noGoodsBody')}
                action={
                  <Button variant="mint" icon={X.Plus} onClick={() => navigate('/cbam/goods')}>
                    {t('cbam.ov.noGoodsCta')}
                  </Button>
                }
              />
            ) : (
              <TableWrap>
                <table className="k2-t">
                  <thead>
                    <tr>
                      <th>CN code</th>
                      <th>Good</th>
                      <th className="k2-r">Route</th>
                      <th className="k2-r">SEE direct</th>
                      <th className="k2-r">SEE indirect</th>
                      <th className="k2-r">Chargeable</th>
                      <th>Values</th>
                    </tr>
                  </thead>
                  <tbody>
                    {goods.map((g) => {
                      const charged = indirectCharged(g);
                      return (
                        <tr key={g.id} onClick={() => onSelect(g)} style={{ cursor: 'pointer' }}>
                          <td className="k2-num" style={{ fontWeight: 600 }}>
                            {g.cn_code}
                            {g.de_minimis_hint && (
                              <span title={t('cbam.ov.deMinimis')} style={{ marginLeft: 6, color: 'var(--n400)', verticalAlign: 'middle', display: 'inline-flex' }}>
                                <X.Info s={13} />
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="k2-nm">{g.description || g.goods_category || '—'}</span>
                            <span className="k2-sub2">{sectorLabel(g.sector)}</span>
                          </td>
                          <td className="k2-r k2-num">{g.production_route || '—'}</td>
                          <td className="k2-r k2-num">{fmt(num(g.see_direct), 3)}</td>
                          <td className="k2-r">
                            <span className="k2-num">{fmt(num(g.see_indirect), 3)}</span>
                            {!charged && (
                              <span style={{ display: 'block', fontSize: 10.5, color: 'var(--n400)' }}>{t('cbam.ov.indirectMemo')}</span>
                            )}
                          </td>
                          <td className="k2-r k2-num" style={{ fontWeight: 600 }}>{fmt(num(g.chargeable_see), 3)}</td>
                          <td>{g.quality ? <Badge tone={QUALITY_TONE[g.quality] || 'out'}>{g.quality}</Badge> : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ padding: '8px 2px 0', fontSize: 11.5, color: 'var(--n400)' }}>
                  SEE in tCO₂e per tonne of good.
                </div>
              </TableWrap>
            )}
          </Card>

          {/* ── cost panel ── */}
          <Card
            title={t('cbam.ov.costTitle')}
            sub={t('cbam.ov.costSub')}
            right={
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--n500)', textAlign: 'right', maxWidth: 160 }}>
                    {t('cbam.ov.etsAssumption')}
                  </span>
                  <UnitInput
                    unit="€/t"
                    type="number"
                    min="0"
                    step="1"
                    invalid={etsInvalid}
                    value={priceOverride === '' ? String(price) : priceOverride}
                    onChange={(e) => setPriceOverride(e.target.value)}
                    style={{ width: 80 }}
                    aria-label="EU ETS price assumption"
                  />
                </div>
                {etsInvalid && (
                  <span className="k2-hint k2-e" style={{ marginTop: 0, textAlign: 'right', maxWidth: 250 }}>
                    <X.Info s={12} />
                    {t('cbam.ov.etsInvalid')}
                  </span>
                )}
                <span style={{ fontSize: 11, color: 'var(--n400)', textAlign: 'right', maxWidth: 250 }}>
                  {t('cbam.ov.etsExportsNote')}
                </span>
              </div>
            }
          >
            {curve.length === 0 ? (
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--n500)' }}>{t('cbam.ov.costEmpty')}</p>
            ) : (
              <>
                <CostCurve years={curve} />
                {goods.length > 1 && (
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10, fontSize: 11.5, color: 'var(--n600)' }}>
                    {goods.map((g, gi) => (
                      <span key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 9, height: 9, borderRadius: 2.5, background: GOOD_COLORS[gi % GOOD_COLORS.length] }} />
                        <span className="k2-num">{g.cn_code}</span>
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
            <div style={{ marginTop: 14 }}>
              <Callout tone="info">{t('cbam.ov.timeline')}</Callout>
            </div>
          </Card>
        </div>

        <div className="k2-gcol">
          {/* ── completeness checklist ── */}
          <Card title={t('cbam.ov.completeTitle')} sub={t('cbam.ov.completeSub')}>
            {completeness.length === 0 || missing.length === 0 ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--ok)' }}>
                <X.Check s={14} />{t('cbam.ov.completeAllOk')}
              </div>
            ) : null}
            {completeness.map((c, i) => (
              <div key={c.key || i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: i < completeness.length - 1 ? '1px solid var(--n100)' : 'none', alignItems: 'flex-start' }}>
                <span style={{ color: c.ok ? 'var(--ok)' : 'var(--warn)', marginTop: 1, flexShrink: 0 }}>
                  {c.ok ? <X.Check s={14} /> : <X.Warn s={14} />}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--n800)' }}>{c.label}</div>
                  {c.detail && <div style={{ fontSize: 11.5, color: 'var(--n500)' }}>{c.detail}</div>}
                </div>
                {!c.ok && (
                  <button type="button" className="k2-lnk" style={{ fontSize: 12 }} onClick={() => navigate(fixRoute(c.key))}>
                    {t('cbam.ov.fix')} →
                  </button>
                )}
              </div>
            ))}
          </Card>

          {/* ── the lever: defaults vs actuals ── */}
          {lever.length > 0 && (
            <Callout tone="warn">
              <b>{t('cbam.ov.defaultsTitle')}</b>{' '}
              {lever.filter((l) => l.deltaSee !== null).map(({ g, deltaSee, eur }) => (
                <span key={g.id} style={{ display: 'block', marginTop: 4 }}>
                  {eur !== null
                    ? t('cbam.ov.defaultsDelta', { cn: g.cn_code, delta: fmt(deltaSee, 3), eur: fmt(eur, 0), year })
                    : t('cbam.ov.defaultsDeltaNoEur', { cn: g.cn_code, delta: fmt(deltaSee, 3) })}
                </span>
              ))}
              <span style={{ display: 'block', marginTop: 6 }}>{t('cbam.ov.defaultsBody')}</span>
            </Callout>
          )}
        </div>
      </div>
    </>
  );
}

function CbamOverviewScreen() {
  const { t } = useT();
  const { toast } = useToast();
  const { hasCbam, resolved } = useSession();

  const [year, setYear] = useState(CBAM_BASE_YEAR);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportInst, setExportInst] = useState('');
  const [exporting, setExporting] = useState(false);
  const [priceOverride, setPriceOverride] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    // The gate shows the upsell to non-entitled users — don't fire a doomed 403.
    if (!resolved || !hasCbam) return undefined;
    let alive = true;
    setLoading(true);
    setError('');
    axios.get(`${API_URL}/api/cbam/summary?year=${year}`, auth())
      .then((r) => { if (alive) setSummary(r.data); })
      .catch((e) => { if (alive) setError(e.response?.data?.message || 'Could not load the CBAM summary.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [year, resolved, hasCbam]);

  const installations = useMemo(() => summary?.installations || [], [summary]);

  /* Keep the export target valid as the summary changes; default to the first installation. */
  useEffect(() => {
    if (installations.length === 0) { setExportInst(''); return; }
    setExportInst((v) => (v && installations.some((i) => String(i.id) === String(v)) ? v : String(installations[0].id)));
  }, [installations]);

  const etsServer = num(summary?.ets_price_eur);
  /**
   * The typed override when set and valid, else null. Every export URL carries
   * it as ?ets_price= so the workbook's and trace PDF's indicative cost columns
   * use the SAME assumption as the screen — null omits the param and the
   * server's env default applies, exactly like the on-screen fallback.
   */
  const etsParam = priceOverride !== '' && Number(priceOverride) > 0 ? Number(priceOverride) : null;
  const price = etsParam ?? etsServer ?? 70;

  const exportXlsx = async () => {
    if (exporting || !exportInst) return;
    setExporting(true);
    try {
      const q = new URLSearchParams({ installation_id: String(exportInst), year: String(year) });
      if (etsParam != null) q.set('ets_price', String(etsParam));
      await downloadFile(`${API_URL}/api/cbam/export/xlsx?${q.toString()}`, `cbam-emissions-report-${year}.xlsx`);
      toast(t('cbam.ov.exported'));
    } catch {
      toast(t('cbam.ov.exportFailed'), { tone: 'err' });
    } finally {
      setExporting(false);
    }
  };

  /* The European Commission's own SEE Communication workbook, prefilled —
   * the file an EU importer or CBAM consultant expects to receive. */
  const exportSeeXlsx = async () => {
    if (exporting || !exportInst) return;
    setExporting(true);
    try {
      const q = new URLSearchParams({ installation_id: String(exportInst), year: String(year) });
      if (etsParam != null) q.set('ets_price', String(etsParam));
      await downloadFile(`${API_URL}/api/cbam/export/see-xlsx?${q.toString()}`, `CBAM-SEE-Communication-${year}.xlsx`);
      toast(t('cbam.ov.exportedSee'));
    } catch {
      toast(t('cbam.ov.exportFailed'), { tone: 'err' });
    } finally {
      setExporting(false);
    }
  };

  const ready = resolved && hasCbam;

  return (
    <div className="k2-wrap">
      <PageHeader title={t('cbam.overview.title')} desc={t('cbam.overview.desc')}>
        {ready && (
          <>
            <Select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 'auto' }} aria-label="Reporting year">
              {CBAM_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </Select>
            {installations.length > 1 && (
              <Select value={exportInst} onChange={(e) => setExportInst(e.target.value)} style={{ width: 'auto', maxWidth: 200 }} aria-label="Installation to export">
                {installations.map((i) => <option key={i.id} value={i.id}>{i.name || `#${i.id}`}</option>)}
              </Select>
            )}
            <Button
              icon={X.Download}
              onClick={exportSeeXlsx}
              disabled={exporting || installations.length === 0}
              title={t('cbam.ov.exportSeeHint')}
            >
              {exporting ? t('cbam.ov.exporting') : t('cbam.ov.exportSee')}
            </Button>
            <Button
              variant="mint"
              icon={X.Download}
              onClick={exportXlsx}
              disabled={exporting || installations.length === 0}
              title={installations.length === 0 ? t('cbam.ov.exportHint') : undefined}
            >
              {exporting ? t('cbam.ov.exporting') : t('cbam.ov.export')}
            </Button>
          </>
        )}
      </PageHeader>
      <CbamGate>
        <CbamOverviewBody
          year={year}
          summary={summary}
          loading={loading}
          error={error}
          price={price}
          priceOverride={priceOverride}
          setPriceOverride={setPriceOverride}
          onSelect={setSelected}
        />
        {selected && <GoodDrawer g={selected} year={year} price={price} etsParam={etsParam} onClose={() => setSelected(null)} />}
      </CbamGate>
    </div>
  );
}

export default function CbamOverviewPage() {
  return (
    <ErrorBoundary>
      <Shell>
        <CbamOverviewScreen />
      </Shell>
    </ErrorBoundary>
  );
}
